import type {
  LifeCheckAnswers,
  LifeCheckHousingSituation,
  LifeCheckIncomeBand,
  LifeCheckPriority,
  LifeCheckRecord,
  LifeCheckResponsibility,
  LifeCheckSection,
  LifeCheckSectionReport,
  LifeCheckSummary,
  LifeCheckSupportType,
  LifeCheckWorkSituation,
} from '@/types';
import { differenceInCalendarDays, parseISO } from 'date-fns';

export const LIFE_CHECK_SECTIONS: Array<{
  key: LifeCheckSection;
  label: string;
  shortLabel: string;
  emoji: string;
  path: string;
}> = [
  { key: 'money', label: 'Money', shortLabel: 'Money', emoji: '💰', path: '/money' },
  { key: 'home', label: 'Home', shortLabel: 'Home', emoji: '🏠', path: '/home' },
  { key: 'health', label: 'Health', shortLabel: 'Health', emoji: '🧠', path: '/health' },
  { key: 'family', label: 'People', shortLabel: 'People', emoji: '👨‍👩‍👧', path: '/family' },
  { key: 'work', label: 'Work', shortLabel: 'Work', emoji: '💼', path: '/work' },
];

export const PRESSURE_LABELS = [
  { value: 1, label: 'Calm' },
  { value: 2, label: 'Holding' },
  { value: 3, label: 'Busy' },
  { value: 4, label: 'Stretched' },
  { value: 5, label: 'On fire' },
] as const;

export const ENERGY_LABELS = [
  { value: 1, label: 'Running on empty' },
  { value: 2, label: 'Very low' },
  { value: 3, label: 'Getting through' },
  { value: 4, label: 'Steady enough' },
  { value: 5, label: 'Solid' },
] as const;

type LifeCheckOption<T extends string> = {
  value: T;
  label: string;
  hint: string;
};

export const LIFE_CHECK_WORK_OPTIONS: LifeCheckOption<LifeCheckWorkSituation>[] = [
  { value: 'employed', label: 'Employed', hint: 'A regular job, salary, or contract is carrying most of the load.' },
  { value: 'self-employed', label: 'Self-employed', hint: 'Invoices, HMRC, and low-month planning matter more.' },
  { value: 'mixed', label: 'A mix of both', hint: 'You have more than one work pattern to keep steady.' },
  { value: 'not-working', label: 'Not working right now', hint: 'Work has stopped, paused, or is not your main focus currently.' },
  { value: 'unstable', label: 'Work feels shaky', hint: 'Hours, income, or job security feel uncertain right now.' },
];

export const LIFE_CHECK_HOUSING_OPTIONS: LifeCheckOption<LifeCheckHousingSituation>[] = [
  { value: 'renting', label: 'Renting', hint: 'Private or social renting, with tenancy and repair admin to stay on top of.' },
  { value: 'owner', label: 'Owner', hint: 'Mortgage, insurance, and renewal timing matter more.' },
  { value: 'living-with-others', label: 'Living with family or friends', hint: 'Shared household admin still needs a calm home.' },
  { value: 'temporary', label: 'Temporary or unstable', hint: 'Housing feels less settled, which usually raises the stakes elsewhere too.' },
];

export const LIFE_CHECK_RESPONSIBILITY_OPTIONS: LifeCheckOption<LifeCheckResponsibility>[] = [
  { value: 'just-me', label: 'Just me', hint: 'No regular caring or shared-household load beyond your own admin.' },
  { value: 'children', label: 'Children', hint: 'School, childcare, forms, deadlines, and family costs are part of the picture.' },
  { value: 'partner-household', label: 'A partner or shared household', hint: 'Bills, chores, and home admin affect more than one person.' },
  { value: 'adult-care', label: 'An adult who needs care', hint: 'There is caring admin for a parent, relative, partner, or another adult.' },
];

export const LIFE_CHECK_INCOME_OPTIONS: LifeCheckOption<LifeCheckIncomeBand>[] = [
  { value: 'under-1500', label: 'Under £1,500', hint: 'Money pressure is usually sharper here, so support checks matter more.' },
  { value: 'between-1500-2500', label: '£1,500 – £2,500', hint: 'There may still be practical support or bill relief worth checking.' },
  { value: 'between-2500-4000', label: '£2,500 – £4,000', hint: 'Less immediate pressure, but admin leakage still adds up.' },
  { value: 'above-4000', label: 'Above £4,000', hint: 'The wins may be smaller, but renewals and overlooked admin still matter.' },
  { value: 'variable', label: 'It varies a lot', hint: 'When income moves around, timing and backup planning matter more.' },
];

export const LIFE_CHECK_SUPPORT_OPTIONS: LifeCheckOption<LifeCheckSupportType>[] = [
  { value: 'none', label: 'None right now', hint: 'You are mostly carrying this without formal support in place.' },
  { value: 'benefits', label: 'Benefits or tax-free help', hint: 'Something is already in place, but it may still be incomplete.' },
  { value: 'family-friends', label: 'Family or friends', hint: 'Informal support helps, but it can still hide care load or admin drift.' },
  { value: 'workplace', label: 'Support through work', hint: 'Your employer or contract is giving some practical support already.' },
  { value: 'council-nhs-charity', label: 'Council, NHS, or charity support', hint: 'You already have some external support routes in the mix.' },
];

const SECTION_META = Object.fromEntries(
  LIFE_CHECK_SECTIONS.map((section) => [section.key, section]),
) as Record<LifeCheckSection, (typeof LIFE_CHECK_SECTIONS)[number]>;

const DEFAULT_FLAGS: Record<LifeCheckSection, string> = {
  money: 'No major money flag jumped out from this check, but it is still worth keeping benefits, budgeting, and recurring payments visible.',
  home: 'Home looks steadier right now, but renewals, repairs, and provider admin still buy breathing room when they stay visible.',
  health: 'No strong health-admin flag showed up here, but reminders and low-level burnout usually drift first when life gets noisy.',
  family: 'People admin looks lighter right now, but shared tasks, care dates, and invisible load are still worth keeping visible.',
  work: 'Work looks steadier right now, but keeping rights, invoices, and deadlines visible still protects future you.',
};

const DEFAULT_NEXT_STEPS: Record<LifeCheckSection, string> = {
  money: 'Open Money and check the budget, benefits, and recurring-payment picture in one place.',
  home: 'Open Home and make sure renewals, repairs, and key dates have somewhere visible to live.',
  health: 'Open Health and keep reminders, medication admin, and your next useful check-in visible.',
  family: 'Open People and make the care, shared-household, or family admin load easier to see.',
  work: 'Open Work and keep rights, invoices, or HMRC admin in one calm place.',
};

type SectionAccumulator = LifeCheckSectionReport & {
  priorityWeight: number;
};

function roundImpact(value: number) {
  return Math.max(0, Math.round(value / 50) * 50);
}

function formatPounds(value: number) {
  return `£${Math.round(value).toLocaleString('en-GB')}`;
}

function createSectionAccumulator(section: (typeof LIFE_CHECK_SECTIONS)[number]): SectionAccumulator {
  return {
    section: section.key,
    label: section.label,
    emoji: section.emoji,
    path: section.path,
    flags: [],
    nextStep: DEFAULT_NEXT_STEPS[section.key],
    impactMin: 0,
    impactMax: 0,
    priorityWeight: 0,
  };
}

function addSectionImpact(
  sections: Record<LifeCheckSection, SectionAccumulator>,
  section: LifeCheckSection,
  config: {
    min: number;
    max: number;
    flag?: string;
    nextStep?: string;
    weight?: number;
  },
) {
  const target = sections[section];
  const weight = config.weight ?? 1;
  const previousWeight = target.priorityWeight;

  target.impactMin += config.min;
  target.impactMax += config.max;
  target.priorityWeight += weight;

  if (config.flag && !target.flags.includes(config.flag)) {
    target.flags.push(config.flag);
  }

  if (config.nextStep && weight >= previousWeight) {
    target.nextStep = config.nextStep;
  }
}

function addGlobalImpact(total: { min: number; max: number }, min: number, max: number) {
  total.min += min;
  total.max += max;
}

function buildSectionReports(answers: LifeCheckAnswers) {
  const sections = Object.fromEntries(
    LIFE_CHECK_SECTIONS.map((section) => [section.key, createSectionAccumulator(section)]),
  ) as Record<LifeCheckSection, SectionAccumulator>;
  const totalImpact = { min: 0, max: 0 };

  switch (answers.workSituation) {
    case 'employed':
      addSectionImpact(sections, 'work', {
        min: 150,
        max: 700,
        flag: 'Keep sick pay, leave, and contract protections visible so nothing sneaks up on you.',
        nextStep: 'Open Work and review your rights, deadlines, and anything that protects your income.',
        weight: 2,
      });
      addGlobalImpact(totalImpact, 200, 800);
      break;
    case 'self-employed':
      addSectionImpact(sections, 'work', {
        min: 500,
        max: 1_800,
        flag: 'Self-employment makes invoices, tax deadlines, and low-month planning more important.',
        nextStep: 'Open Work and check invoices, HMRC dates, and any gaps in low-month planning.',
        weight: 4,
      });
      addSectionImpact(sections, 'money', {
        min: 300,
        max: 1_000,
        flag: 'Irregular cash flow can hide pressure until it suddenly becomes urgent.',
        nextStep: 'Open Money and make sure your budget and recurring commitments still fit the current month.',
        weight: 2,
      });
      addGlobalImpact(totalImpact, 800, 2_200);
      break;
    case 'mixed':
      addSectionImpact(sections, 'work', {
        min: 450,
        max: 1_600,
        flag: 'A mixed work setup usually means more moving parts, deadlines, and admin friction.',
        nextStep: 'Open Work and keep the paid-work picture in one place before it fragments.',
        weight: 3,
      });
      addSectionImpact(sections, 'money', {
        min: 200,
        max: 800,
        flag: 'Multiple income patterns make it easier to miss what is actually coming in each month.',
        nextStep: 'Open Money and check whether the budget still matches the reality of your month.',
        weight: 2,
      });
      addGlobalImpact(totalImpact, 700, 2_000);
      break;
    case 'not-working':
      addSectionImpact(sections, 'money', {
        min: 1_800,
        max: 4_800,
        flag: 'Not working right now usually means the biggest chance of missing support or cost relief.',
        nextStep: 'Open Money and run the benefits and support checks first.',
        weight: 5,
      });
      addSectionImpact(sections, 'work', {
        min: 150,
        max: 500,
        flag: 'If work changed recently, keep letters, deadlines, and next-step admin close together.',
        nextStep: 'Open Work if the change is recent and you need the employment-rights or evidence side kept visible.',
        weight: 1,
      });
      addGlobalImpact(totalImpact, 1_800, 5_200);
      break;
    case 'unstable':
      addSectionImpact(sections, 'work', {
        min: 650,
        max: 2_000,
        flag: 'If work feels shaky, rights, notice periods, and paper trails matter much more.',
        nextStep: 'Open Work and start with the rights and protection side before things get noisier.',
        weight: 4,
      });
      addSectionImpact(sections, 'money', {
        min: 450,
        max: 1_400,
        flag: 'Income uncertainty makes it more important to see support, bills, and recurring outgoings clearly.',
        nextStep: 'Open Money and check the pressure points before they stack up.',
        weight: 3,
      });
      addGlobalImpact(totalImpact, 900, 2_800);
      break;
    default:
      break;
  }

  switch (answers.housingSituation) {
    case 'renting':
      addSectionImpact(sections, 'home', {
        min: 350,
        max: 1_300,
        flag: 'Renting brings tenancy, repair, and provider admin that is easy to carry silently.',
        nextStep: 'Open Home and make sure rent, repairs, and key home renewals are visible.',
        weight: 3,
      });
      addSectionImpact(sections, 'money', {
        min: 150,
        max: 500,
        flag: 'Rent pressure can squeeze the rest of the budget quickly when bills or support change.',
        nextStep: 'Open Money and check what the rest of the month has to carry after housing costs.',
        weight: 1,
      });
      addGlobalImpact(totalImpact, 300, 1_200);
      break;
    case 'owner':
      addSectionImpact(sections, 'home', {
        min: 200,
        max: 900,
        flag: 'Owning usually means renewal timing, insurance, and maintenance can quietly become expensive.',
        nextStep: 'Open Home and keep mortgage, insurance, and maintenance dates visible.',
        weight: 2,
      });
      addGlobalImpact(totalImpact, 100, 700);
      break;
    case 'living-with-others':
      addSectionImpact(sections, 'home', {
        min: 0,
        max: 400,
        flag: 'Shared households still benefit from clear bills, chores, and home admin.',
        nextStep: 'Open Home and keep the shared-household admin from becoming invisible.',
        weight: 1,
      });
      addSectionImpact(sections, 'family', {
        min: 100,
        max: 400,
        flag: 'Shared living arrangements can create admin load even when nobody calls it “care”.',
        nextStep: 'Open People if shared-household tasks or caring work are landing unevenly.',
        weight: 1,
      });
      addGlobalImpact(totalImpact, 0, 300);
      break;
    case 'temporary':
      addSectionImpact(sections, 'home', {
        min: 1_200,
        max: 3_200,
        flag: 'Temporary or unstable housing usually raises the stakes across the rest of life admin too.',
        nextStep: 'Open Home first and get the most urgent housing admin into one place.',
        weight: 5,
      });
      addSectionImpact(sections, 'money', {
        min: 350,
        max: 1_200,
        flag: 'Housing instability often changes what practical money support is worth checking.',
        nextStep: 'Open Money after Home and check for immediate support or relief routes.',
        weight: 3,
      });
      addGlobalImpact(totalImpact, 1_200, 3_000);
      break;
    default:
      break;
  }

  if (answers.responsibilities.includes('just-me')) {
    addSectionImpact(sections, 'health', {
      min: 100,
      max: 350,
      flag: 'When everything sits on one person, admin usually slips first when energy gets tight.',
      nextStep: 'Open Health and keep the small reminders and check-ins easy to see.',
      weight: 1,
    });
    addGlobalImpact(totalImpact, 0, 150);
  }

  if (answers.responsibilities.includes('children')) {
    addSectionImpact(sections, 'family', {
      min: 1_300,
      max: 4_000,
      flag: 'Children usually change entitlement, school admin, childcare costs, and daily invisible load.',
      nextStep: 'Open People and get the childcare, school, or family-admin load out of your head.',
      weight: 5,
    });
    addSectionImpact(sections, 'money', {
      min: 350,
      max: 1_200,
      flag: 'Children can change what financial support or cost relief is on the table.',
      nextStep: 'Open Money and check whether family-related support is being missed.',
      weight: 3,
    });
    addGlobalImpact(totalImpact, 1_200, 3_800);
  }

  if (answers.responsibilities.includes('partner-household')) {
    addSectionImpact(sections, 'family', {
      min: 200,
      max: 900,
      flag: 'Shared-household admin can still become invisible load even when it is not dramatic.',
      nextStep: 'Open People and make the shared-admin picture easier to see and divide.',
      weight: 2,
    });
    addSectionImpact(sections, 'home', {
      min: 100,
      max: 450,
      flag: 'More than one person in the household usually means more loose ends around bills, chores, and renewals.',
      nextStep: 'Open Home and keep the shared-home admin visible.',
      weight: 1,
    });
    addGlobalImpact(totalImpact, 200, 800);
  }

  if (answers.responsibilities.includes('adult-care')) {
    addSectionImpact(sections, 'family', {
      min: 550,
      max: 2_100,
      flag: 'Caring for another adult often unlocks support that people miss because nobody labels them as a carer.',
      nextStep: 'Open People and get the caring load, reviews, or practical tasks into one place.',
      weight: 4,
    });
    addSectionImpact(sections, 'health', {
      min: 150,
      max: 700,
      flag: 'Caring admin often lands in the same mental bucket as your own health and energy.',
      nextStep: 'Open Health and keep your own reminders and check-ins visible too.',
      weight: 2,
    });
    addGlobalImpact(totalImpact, 500, 1_800);
  }

  switch (answers.monthlyIncomeBand) {
    case 'under-1500':
      addSectionImpact(sections, 'money', {
        min: 1_800,
        max: 4_400,
        flag: 'Lower monthly income usually means the biggest chance of missed support or bill relief.',
        nextStep: 'Open Money and start with the benefits and pressure-relief side first.',
        weight: 5,
      });
      addGlobalImpact(totalImpact, 1_800, 4_500);
      break;
    case 'between-1500-2500':
      addSectionImpact(sections, 'money', {
        min: 800,
        max: 2_300,
        flag: 'There may still be useful support or cost savings worth checking from this starting point.',
        nextStep: 'Open Money and work through the practical support and recurring-payment picture.',
        weight: 4,
      });
      addGlobalImpact(totalImpact, 700, 1_800);
      break;
    case 'between-2500-4000':
      addSectionImpact(sections, 'money', {
        min: 250,
        max: 900,
        flag: 'The wins may be smaller here, but avoidable admin leakage still adds up over a year.',
        nextStep: 'Open Money and keep budget drift and recurring costs visible.',
        weight: 2,
      });
      addGlobalImpact(totalImpact, 200, 900);
      break;
    case 'above-4000':
      addSectionImpact(sections, 'money', {
        min: 0,
        max: 450,
        flag: 'Pressure may be lower, but renewals, recurring costs, and overlooked admin still matter.',
        nextStep: 'Open Money and check whether the quiet leaks are still worth tightening.',
        weight: 1,
      });
      addGlobalImpact(totalImpact, 0, 400);
      break;
    case 'variable':
      addSectionImpact(sections, 'money', {
        min: 900,
        max: 2_600,
        flag: 'Variable income makes timing, buffers, and recurring commitments much more important.',
        nextStep: 'Open Money and check the current budget against a realistic low month, not just a good one.',
        weight: 4,
      });
      addSectionImpact(sections, 'work', {
        min: 250,
        max: 800,
        flag: 'When income moves around, Work and Money need to stay joined up instead of living in separate buckets.',
        nextStep: 'Open Work and make sure invoice or workload admin is still supporting the month ahead.',
        weight: 2,
      });
      addGlobalImpact(totalImpact, 900, 2_200);
      break;
    default:
      break;
  }

  if (answers.supportTypes.includes('none')) {
    addSectionImpact(sections, 'money', {
      min: 600,
      max: 2_000,
      flag: 'If no support is in place yet, the first scan usually finds the clearest wins.',
      nextStep: 'Open Money and start with the support routes you are not already receiving.',
      weight: 4,
    });
    addSectionImpact(sections, 'health', {
      min: 100,
      max: 300,
      flag: 'Carrying everything without backup can make the rest of the admin feel heavier than it looks on paper.',
      nextStep: 'Open Health and keep the smallest useful reminders visible, not perfect.',
      weight: 1,
    });
    addGlobalImpact(totalImpact, 500, 1_800);
  } else {
    if (answers.supportTypes.includes('benefits')) {
      addSectionImpact(sections, 'money', {
        min: 150,
        max: 700,
        flag: 'Already getting support is good, but changes in circumstances can still move the number.',
        nextStep: 'Open Money and sense-check that current support still matches your situation.',
        weight: 2,
      });
      addGlobalImpact(totalImpact, 100, 500);
    }

    if (answers.supportTypes.includes('family-friends')) {
      addSectionImpact(sections, 'family', {
        min: 0,
        max: 350,
        flag: 'Informal support helps, but it can still hide how much ongoing care or admin is sitting with you.',
        nextStep: 'Open People and make the invisible caring/admin load easier to see.',
        weight: 1,
      });
      addGlobalImpact(totalImpact, 0, 300);
    }

    if (answers.supportTypes.includes('workplace')) {
      addSectionImpact(sections, 'work', {
        min: 0,
        max: 300,
        flag: 'Some support is already coming through work, so focus on keeping dates, paperwork, and rights visible.',
        nextStep: 'Open Work and keep the protection already in place from drifting out of view.',
        weight: 1,
      });
      addGlobalImpact(totalImpact, 0, 300);
    }

    if (answers.supportTypes.includes('council-nhs-charity')) {
      addSectionImpact(sections, 'home', {
        min: 0,
        max: 250,
        flag: 'You already have some outside support in the mix, so the next win is often keeping it current and easy to find again.',
        nextStep: 'Open Home and keep the practical support paperwork and dates from going stale.',
        weight: 1,
      });
      addSectionImpact(sections, 'health', {
        min: 0,
        max: 250,
        flag: 'Existing support is a good sign; the next job is making sure nothing useful expires quietly.',
        nextStep: 'Open Health and keep the next review, reminder, or follow-up visible.',
        weight: 1,
      });
      addGlobalImpact(totalImpact, 0, 300);
    }
  }

  const sectionReports = LIFE_CHECK_SECTIONS.map(({ key }) => {
    const section = sections[key];
    return {
      ...section,
      impactMin: roundImpact(section.impactMin),
      impactMax: roundImpact(section.impactMax),
      flags: section.flags.length ? section.flags.slice(0, 2) : [DEFAULT_FLAGS[key]],
    };
  });

  return { sectionReports, totalImpact };
}

export function buildEmptyLifeCheckAnswers(): LifeCheckAnswers {
  return {
    workSituation: null,
    housingSituation: null,
    responsibilities: [],
    monthlyIncomeBand: null,
    supportTypes: [],
    note: '',
  };
}

export function isLifeCheckComplete(answers: LifeCheckAnswers) {
  return Boolean(
    answers.workSituation
      && answers.housingSituation
      && answers.monthlyIncomeBand
      && answers.responsibilities.length > 0
      && answers.supportTypes.length > 0,
  );
}

export function formatLifeCheckImpactRange(summary: LifeCheckSummary | null | undefined) {
  const min = summary?.estimatedImpactMin;
  const max = summary?.estimatedImpactMax;
  if (typeof min !== 'number' || typeof max !== 'number') {
    return 'Impact estimate unavailable';
  }

  return `${formatPounds(min)} – ${formatPounds(max)} a year`;
}

export function getLifeCheckImpactAverage(summary: LifeCheckSummary | null | undefined) {
  const min = summary?.estimatedImpactMin;
  const max = summary?.estimatedImpactMax;

  if (typeof min !== 'number' || typeof max !== 'number') {
    return null;
  }

  return (min + max) / 2;
}

export function describeLifeCheckImpactChange(
  current: LifeCheckSummary | null | undefined,
  previous: LifeCheckSummary | null | undefined,
) {
  const currentAverage = getLifeCheckImpactAverage(current);
  const previousAverage = getLifeCheckImpactAverage(previous);

  if (currentAverage === null || previousAverage === null) {
    return null;
  }

  const difference = roundImpact(currentAverage - previousAverage);

  if (Math.abs(difference) < 300) {
    return 'About the same as your last check.';
  }

  if (difference < 0) {
    return `${formatPounds(Math.abs(difference))} lower than your last check.`;
  }

  return `${formatPounds(difference)} higher than your last check.`;
}

export function buildLifeCheckShareText(summary: LifeCheckSummary | null | undefined) {
  if (!summary) {
    return 'Run your Adulting Life Check to get a personalised support snapshot.';
  }

  const impact = formatLifeCheckImpactRange(summary);
  const findings = (summary.priorities || [])
    .slice(0, 3)
    .map((priority) => `- ${priority.label}: ${priority.reason}`)
    .join('\n');

  return [
    'Adulting Life Check',
    `Estimated financial impact: ${impact.replace(' a year', ' per year')}`,
    summary.headline,
    findings || '- Run a fresh Life Check to generate your current headline findings.',
    'Run your own: https://adultingapp.uk/life-check',
  ].join('\n\n');
}

export function buildLifeCheckSummary(answers: LifeCheckAnswers): LifeCheckSummary {
  const { sectionReports, totalImpact } = buildSectionReports(answers);
  const estimatedImpactMin = Math.min(9_500, roundImpact(totalImpact.min));
  const estimatedImpactMax = Math.max(estimatedImpactMin, Math.min(12_000, roundImpact(totalImpact.max)));

  const priorities: LifeCheckPriority[] = [...sectionReports]
    .sort((a, b) => b.impactMax - a.impactMax || b.flags.length - a.flags.length)
    .slice(0, 3)
    .map((report) => ({
      section: report.section,
      label: report.label,
      emoji: report.emoji,
      path: report.path,
      score: Math.max(1, Math.min(5, Math.ceil(report.impactMax / 1_200))),
      reason: report.flags[0] || report.nextStep,
    }));

  const urgentSections = priorities.slice(0, 2).map((priority) => priority.section);
  const topSection = priorities[0]?.section ?? 'money';

  let headline = 'The Life Check found a few worthwhile next steps, even if nothing looks like a cliff-edge right now.';
  if (estimatedImpactMax >= 6_000) {
    headline = 'Adulting thinks there may be meaningful support, savings, or admin relief still being missed right now.';
  } else if (estimatedImpactMax >= 3_000) {
    headline = 'There looks like a useful amount of practical support or admin relief still worth checking.';
  } else if (estimatedImpactMax >= 1_500) {
    headline = 'There are a few clear next steps here that could buy you breathing room fairly quickly.';
  }

  const summary: LifeCheckSummary = {
    headline,
    priorities,
    urgentSections,
    overallLoad: Number((estimatedImpactMax / 2_000).toFixed(1)),
    recommendedPath: SECTION_META[topSection].path,
    estimatedImpactMin,
    estimatedImpactMax,
    sectionReports,
  };

  return {
    ...summary,
    shareText: buildLifeCheckShareText(summary),
  };
}

export function getTopLifeCheckPriority(summary: LifeCheckSummary | null | undefined) {
  return Array.isArray(summary?.priorities) && summary.priorities.length > 0
    ? summary.priorities[0]
    : null;
}

export function getLifeCheckAgeDays(record: LifeCheckRecord | null | undefined) {
  if (!record?.createdAt) {
    return null;
  }

  return differenceInCalendarDays(new Date(), parseISO(record.createdAt));
}

export function isLifeCheckStale(record: LifeCheckRecord | null | undefined, staleAfterDays = 14) {
  const ageDays = getLifeCheckAgeDays(record);
  return ageDays === null ? true : ageDays >= staleAfterDays;
}
